import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageService } from 'primeng/api';
import { ESignService } from 'src/app/services/e-sign.service'; // Import ESignService
import { UrlValidatorService } from 'src/app/services/url-validator.service';
import { DomainType } from 'src/app/enum/domain-type.enum';

@Component({
    selector: 'app-dsc-response',
    templateUrl: './dsc-response.component.html',
    styleUrls: ['./dsc-response.component.scss'],
    standalone: false
})
export class DscResponseComponent implements OnInit {
  status: string;
  isEmailButtonDisabled: boolean = false;
  emailCooldownTime: number = 0;
  queryParams: any;
  signerData: any = [];
  private emailCooldownInterval: any;
  esignId: string;
  signatoryId: string;

  signatoryDetails: any = {};

  constructor(
    private spinner: NgxSpinnerService,
    private activeRouter: ActivatedRoute,
    private eSignService: ESignService,
    private messageService: MessageService,
    private urlValidator: UrlValidatorService,
  ) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    this.status = urlSearchParams.get('status');
    this.activeRouter.queryParams.subscribe((queryParams) => {
      this.queryParams = queryParams;
      this.esignId = this.queryParams.esign_id;
      this.signatoryId = this.queryParams.signatory_id;
    });
  }

ngOnInit(): void {
  this.spinner.hide();

  const urlSearchParams = new URLSearchParams(window.location.search);
  const redirect = urlSearchParams.get('redirect');
  const signatoryId = urlSearchParams.get('signatory_id');
  const orderId = urlSearchParams.get('order_id');

  if (
    (this.status === 'success' || this.status === 'failed') &&
    redirect === 'true' &&
    signatoryId &&
    orderId
  ) {
    this.eSignService.getExternalSignLink(signatoryId, orderId).subscribe(
      (res) => {
        if (res['message'] && res['message'].includes('Image not captured')) {
          this.messageService.add({ severity: 'warn', detail: res['message'] });
          setTimeout(() => {
            window.location.reload();
          }, 5000);
          return;
        }

        const redirectUrl = res['content']?.redirect_url;

        if (redirectUrl && this.urlValidator.isValidRedirectUrl(redirectUrl, DomainType.EMUDHRA)) {
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1000);
        } else {
          console.error('Invalid or unsafe redirect URL blocked');
          this.handleFallback();
        }
      },
      (error) => {
        console.error('Error fetching external sign link:', error);
        const errMsg = error?.error?.message || '';
        if (errMsg.includes('Image not captured')) {
          this.messageService.add({ severity: 'error', detail: errMsg });
          setTimeout(() => {
            window.location.reload();
          }, 5000);
          return;
        }
        this.handleFallback();
      }
    );
  } else {
    this.handleFallback();
  }
  this.getSignerDetails();
}

  private handleFallback() {
    // Fallback behavior: close the window
    window.close();
  }

  getSignerDetails() {
    this.eSignService.getSignerDetails(this.queryParams).subscribe((res) => {
      this.signerData = res['content'];
    });
    this.spinner.hide();
  }

  shareToEmail() {
    if (this.isEmailButtonDisabled) {
      this.messageService.add({
        severity: 'warn',
        detail: `Please wait ${this.emailCooldownTime} seconds before sending again.`,
        life: 3000,
      });
      return;
    }

    const payload = {
      esign_id: this.queryParams.esign_id,
      signatory_id: this.queryParams.signatory_id,
      notify_type: 'ESIGN_SUCCESS',
    };

    this.isEmailButtonDisabled = true;

    this.eSignService.sendEmailNotification(payload).subscribe({
      next: (response) => {
        // console.log('Email notification sent successfully', response);
        this.messageService.add({
          severity: 'success',
          detail: 'Signed document has been sent to your email successfully.',
          life: 3500,
        });

        // Start cooldown timer
        this.startEmailCooldown();
      },
      error: (error) => {
        console.error('Error sending email notification', error);
        this.messageService.add({
          severity: 'error',
          detail: 'Failed to send email. Please try again.',
          life: 3500,
        });
        // Re-enable button on error
        this.isEmailButtonDisabled = false;
      },
    });
  }

  startEmailCooldown() {
    this.emailCooldownTime = 10;

    this.emailCooldownInterval = setInterval(() => {
      this.emailCooldownTime--;

      if (this.emailCooldownTime <= 0) {
        this.isEmailButtonDisabled = false;
        clearInterval(this.emailCooldownInterval);
      }
    }, 1000);
  }

  printDocument() {
    const documentBase64 = this.signerData?.document;
    // console.log('document===========>', documentBase64);

    if (documentBase64) {
      try {
        const byteArray = new Uint8Array(
          atob(documentBase64)
            .split('')
            .map((char) => char.charCodeAt(0))
        );
        const file = new Blob([byteArray], {
          type: 'application/pdf',
        });
        const fileURL = URL.createObjectURL(file);

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.visibility = 'hidden';

        document.body.appendChild(iframe);
        iframe.src = fileURL;

        iframe.onload = () => {
          setTimeout(() => {
            try {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();

              const cleanupHandler = () => {
                setTimeout(() => {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                  }
                  URL.revokeObjectURL(fileURL);
                }, 100);
              };

              iframe.contentWindow?.addEventListener(
                'afterprint',
                cleanupHandler
              );

              setTimeout(cleanupHandler, 60000); // 60 seconds fallback
            } catch (e) {
              console.error('Print error:', e);
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
              }
              URL.revokeObjectURL(fileURL);
            }
          }, 500);
        };

        // console.log('Printing document...');
      } catch (error) {
        // console.error('Error printing document:', error);
      }
    } else {
      // console.error('Document not available for printing');
    }
  }

  downloadDocument() {
    const documentBase64 = this.signerData?.document;
    // console.log('document===========>', documentBase64);
    const esignId = this.signatoryDetails?.esign_id;

    if (documentBase64) {
      try {
        const byteArray = new Uint8Array(
          atob(documentBase64)
            .split('')
            .map((char) => char.charCodeAt(0))
        );
        const file = new Blob([byteArray], {
          type: 'application/pdf',
        });
        const fileURL = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = fileURL;

        const sanitizedId = esignId
          ? esignId.replace(/ /g, '_')
          : `signed_document_${Date.now()}`;

        const filename = `${sanitizedId}.pdf`;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(fileURL);
      } catch (error) {
        console.error('Error downloading document:', error);
      }
    } else {
      // console.error('Document not available in signerData');
    }
  }
}
